import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';

const AddParkingZoneModal = ({ isOpen, zone, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    zoneId: '',
    city: '',
    zoneName: '',
    smsCode: '',
    smsPhone: '',
    ourPrice: '',
    ourCurrency: 'EUR',
    timeInMinutes: 60,
    streets: []
  });
  const [newStreet, setNewStreet] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (zone) {
      setFormData({
        zoneId: zone.zoneId || '',
        city: zone.city || '',
        zoneName: zone.zoneName || '',
        smsCode: zone.smsCode || '',
        smsPhone: zone.smsPhone || '',
        ourPrice: zone.ourPrice || '',
        ourCurrency: zone.ourCurrency || 'EUR',
        timeInMinutes: zone.timeInMinutes || 60,
        streets: zone.streets || []
      });
    } else {
      // Reset form for new zone
      setFormData({
        zoneId: '',
        city: '',
        zoneName: '',
        smsCode: '',
        smsPhone: '',
        ourPrice: '',
        ourCurrency: 'EUR',
        timeInMinutes: 60,
        streets: []
      });
    }
  }, [zone, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddStreet = () => {
    if (newStreet.trim()) {
      setFormData(prev => ({
        ...prev,
        streets: [...prev.streets, newStreet.trim()]
      }));
      setNewStreet('');
    }
  };

  const handleRemoveStreet = (index) => {
    setFormData(prev => ({
      ...prev,
      streets: prev.streets.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const client = getApiClient();
      const endpoint = zone 
        ? '/admin/parking/update-zone'
        : '/admin/parking/add-zone';

      const payload = {
        ...formData,
        ourPrice: parseFloat(formData.ourPrice),
        currency: 'RON', // User currency as per DTO
        timeInMinutes: parseInt(formData.timeInMinutes)
      };

      // Remove zoneId if creating new zone
      if (!zone) {
        delete payload.zoneId;
      }

      await (zone ? client.put(endpoint, payload) : client.post(endpoint, payload));

      alert(zone ? t('zoneUpdatedSuccessfully') : t('zoneAddedSuccessfully'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving parking zone:', error);
      alert(t('errorSavingZone') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">{t('close')}</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-6">
                      {zone ? t('editParkingZone') : t('addNewParkingZone')}
                    </Dialog.Title>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Basic Information */}
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="zoneName" className="block text-sm font-medium text-gray-700">
                            {t('zoneName')} *
                          </label>
                          <input
                            type="text"
                            name="zoneName"
                            id="zoneName"
                            required
                            value={formData.zoneName}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                            {t('city')} *
                          </label>
                          <input
                            type="text"
                            name="city"
                            id="city"
                            required
                            value={formData.city}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="smsCode" className="block text-sm font-medium text-gray-700">
                            {t('smsCode')} *
                          </label>
                          <input
                            type="text"
                            name="smsCode"
                            id="smsCode"
                            required
                            value={formData.smsCode}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="smsPhone" className="block text-sm font-medium text-gray-700">
                            {t('smsPhone')} *
                          </label>
                          <input
                            type="text"
                            name="smsPhone"
                            id="smsPhone"
                            required
                            value={formData.smsPhone}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="ourPrice" className="block text-sm font-medium text-gray-700">
                            {t('ourPrice')} *
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <input
                              type="number"
                              name="ourPrice"
                              id="ourPrice"
                              required
                              step="0.01"
                              min="0"
                              value={formData.ourPrice}
                              onChange={handleInputChange}
                              className="block w-full rounded-md border-gray-300 pr-12 focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">{formData.ourCurrency}</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label htmlFor="ourCurrency" className="block text-sm font-medium text-gray-700">
                            {t('currency')}
                          </label>
                          <select
                            name="ourCurrency"
                            id="ourCurrency"
                            value={formData.ourCurrency}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          >
                            <option value="RON">RON</option>
                            <option value="EUR">EUR</option>
                            <option value="USD">USD</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="timeInMinutes" className="block text-sm font-medium text-gray-700">
                            {t('duration')} ({t('minutes')}) *
                          </label>
                          <input
                            type="number"
                            name="timeInMinutes"
                            id="timeInMinutes"
                            required
                            min="1"
                            value={formData.timeInMinutes}
                            onChange={handleInputChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      {/* Streets Section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('streets')}
                        </label>
                        <div className="flex gap-2 mb-3">
                          <input
                            type="text"
                            value={newStreet}
                            onChange={(e) => setNewStreet(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddStreet();
                              }
                            }}
                            placeholder={t('enterStreetName')}
                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                          />
                          <button
                            type="button"
                            onClick={handleAddStreet}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <PlusIcon className="h-5 w-5" />
                          </button>
                        </div>

                        {formData.streets.length > 0 && (
                          <div className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
                            <ul className="space-y-2">
                              {formData.streets.map((street, index) => (
                                <li key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                                  <span className="text-gray-700">{street}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveStreet(index)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                        <button
                          type="submit"
                          disabled={loading}
                          className="inline-flex w-full justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? t('saving') : zone ? t('updateZone') : t('addZone')}
                        </button>
                        <button
                          type="button"
                          onClick={onClose}
                          disabled={loading}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('cancel')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddParkingZoneModal;
